/**
 * Date-only formatting for the store surfaces, and the delivery-window string.
 *
 * Lives at the store root rather than under `orders/` or `quotes/` because both
 * detail screens, both list screens and the collection form all render the same
 * kind of value. Second usage = lift it (AGENTS.md rule 15); a sibling import
 * across feature folders would be the alternative, and `quote-format.ts`
 * re-exporting `../orders/order-format.ts` is already one too many.
 *
 * **Everything here is pinned to UTC, deliberately.** A `dispatch_by`, an
 * `expires_on` or a delivery window is a *calendar day*, not a moment, and the
 * wire carries it as `yyyy-mm-dd`. `new Date("2026-07-20")` is UTC midnight, so
 * formatting it in the viewer's zone renders the previous day anywhere west of
 * UTC: a CI runner in New York reads "19 Jul" where the operator in Surat reads
 * "20 Jul". Spec 16 AC6 freezes exactly that date.
 *
 * Timestamps are a different question and keep their own formatter — see
 * `shortDate` in `orders/order-format.ts`, which is right to render `createdAt`
 * and `sentAt` in the viewer's zone.
 */

/** Shared by every formatter here so there is one place the zone is decided. */
const DAY = { day: "numeric", month: "short", timeZone: "UTC" } as const;
const DAY_YEAR = { ...DAY, year: "numeric" } as const;

/**
 * "30 Nov" — a calendar day in a table cell or a meta row.
 *
 * Takes either shape a calendar day arrives in: a bare `yyyy-mm-dd` (`dispatch_by`,
 * `expires_on`, a delivery window) **or** a full ISO instant from a `timestamptz`
 * column that stores days at UTC midnight (`store_collections.active_from`). Both
 * are correct here, because that storage convention is invariant across the
 * codebase — the seed writes UTC midnight via `tsOffset`, the collection form
 * writes `T00:00:00.000Z` via `fromDateInput`, and `collectionPhase` compares
 * through `toIstDate` on the same assumption. If a true IST-midnight instant
 * (`…T18:30:00Z`) ever got written, the phase comparison would break alongside
 * this formatter — it is a system-wide invariant, not a property of one call site.
 */
export function shortDay(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", DAY);
}

/** "30 Nov 2026" — the same, when the year carries information. */
export function shortDayYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", DAY_YEAR);
}

/**
 * The promised delivery window (spec 16 R2) — "1–30 Nov 2026".
 *
 * Frozen by spec 16 §7/AC1, so the shape is not free to drift: an en-dash with
 * no spaces, and the month and year said once when both ends share them. The
 * variants:
 *
 *   same month + year   "1–30 Nov 2026"
 *   same year           "28 Oct – 5 Nov 2026"
 *   different years     "20 Dec 2026 – 5 Jan 2027"
 *   single day          "30 Nov 2026"          (§5 allows a one-day window)
 *   half a window       "—"                    (R2 forbids it; see below)
 *
 * A half-open window returns an em-dash rather than "From 1 Nov": R2 requires
 * both ends, so one end present means the row is malformed, and a half-truth
 * reads as a real promise. `seasonWindow` in `collections/collection-format.ts`
 * deliberately does the opposite for *seasons*, where an open end was once
 * legal — different rule, different function, shared `shortDay` underneath.
 */
export function deliveryWindow(
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
): string {
  if (!startsOn || !endsOn) return "—";

  const start = new Date(startsOn);
  const end = new Date(endsOn);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "—";

  if (startsOn === endsOn) return shortDayYear(endsOn);

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  const sameMonth = sameYear && start.getUTCMonth() === end.getUTCMonth();

  // Within one month the day numbers sit either side of an en-dash and the
  // month/year is said once — "1–30 Nov 2026", the frozen AC1 string.
  if (sameMonth) return `${start.getUTCDate()}–${shortDayYear(endsOn)}`;
  if (sameYear) return `${shortDay(startsOn)} – ${shortDayYear(endsOn)}`;
  return `${shortDayYear(startsOn)} – ${shortDayYear(endsOn)}`;
}
